import { useState } from 'react'
import { Alert, Form, Input, Modal, Select, Typography } from 'antd'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { didChangeState, type OperationResult } from '@/shared/api/operationResult'
// Paketlar `shared/api/reference` dan: `features/*` bir-birini import
// qilmaydi (07-FRONTEND.md §6), katalog esa bu yerda faqat tanlov ro'yxati
import { useDeniedPlans, usePlanOptions, useVispOptions } from '@/shared/api/reference'
import { ContactFields } from '@/features/contact/ContactFields'
import {
  buildContactRequest,
  createContact,
  type ContactFieldValues,
} from '@/features/contact/contactApi'
import { displayName } from '@/shared/lib/planName'
import { useAuth } from '@/shared/auth/useAuth'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { branchLabel } from '@/features/branch/branchApi'
import { usePermissions } from '@/shared/auth/usePermissions'
import { getCurrencyCode } from '@/shared/lib/format'
import { formatMoneyValue, type Money } from '@/shared/lib/money'
import { ExpandableHint } from '@/shared/ui/ExpandableHint'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { OperationResultAlert } from '@/shared/ui/OperationResultAlert'
import { SubscriberFormFields, type SubscriberFieldValues } from './SubscriberFormFields'
import { SubscriberProfileFields } from './SubscriberProfileFields'
import { PoolAccountSelect } from './PoolAccountSelect'
import { VispSelect } from './VispSelect'
import { profileDraft, type ProfileFieldValues } from './profileForm'
import { normalizePhone, provisionSubscriber, registerPoolAccount, type DeliveryMethod } from './subscriberApi'
import { submitOnValid } from '@/shared/lib/submitOnValid'

interface CreateSubscriberModalProps {
  open: boolean
  onClose: () => void
}

/** Umumiy maydonlar `SubscriberFormFields` da; bu yerda faqat SOTUVGA oid qismi */
interface FormValues extends SubscriberFieldValues {
  password?: string
  planId?: string
  topUp?: Money | null
  note?: string
  /** Kontakt `platform.contact` ga ALOHIDA so'rov bilan yoziladi — pastga qarang */
  contact?: ContactFieldValues
  profile?: ProfileFieldValues
  vispId?: string
  branchNodeId?: string
}

/**
 * Yangi abonent.
 *
 * NEGA `POST /api/subscribers` EMAS, `/provision`: birinchisi FAQAT hisob
 * ochadi — paketsiz va pulsiz. Amalda abonent deyarli har doim darhol paket
 * sotib oladi, va uni ikki chaqiruvda qilish yarim yaratilgan abonent
 * qoldirish xavfini tug'diradi (hisob ochildi, to'lov yiqildi). `/provision`
 * uchalasini BITTA TRANZAKSIYADA bajaradi.
 *
 * Paket tanlanmasa `planId` bo'sh ketadi va backend faqat hisob ochadi —
 * ya'ni ikkinchi endpoint kerak emas.
 */
export function CreateSubscriberModal({ open, onClose }: CreateSubscriberModalProps) {
  const { t, i18n } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { can } = usePermissions()
  const canWriteContact = can('contact.write')
  const canReadVisps = can('network.visp.read')
  const canPickVisp = canReadVisps && can('subscriber.visp.assign')
  const vispOptions = useVispOptions(open && canReadVisps)
  const defaultVisp = vispOptions.find((v) => v.isDefault)
  const selectedVispId = Form.useWatch('vispId', form)
  const poolVisp = vispOptions.find((v) => v.id === selectedVispId && v.pooled)
  const { options: branchChoices, selected: activeBranches } = useActiveBranches()
  const tenantWide = user?.scopes.some((scope) => scope.type === 'TENANT' || scope.type === 'GLOBAL') ?? false
  const branchRequired = !tenantWide && branchChoices.length > 1
  const showBranch = tenantWide ? branchChoices.length > 0 : branchChoices.length > 1

  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<OperationResult | null>(null)
  const [contactError, setContactError] = useState<string | null>(null)

  // Faqat SOTILADIGAN paketlar: DRAFT va ARCHIVED ro'yxatda ko'rinsa,
  // operator ularni tanlab, keyin backend rad javobini olardi
  const chosenBranch = Form.useWatch('branchNodeId', form)
  const denied = useDeniedPlans(chosenBranch ?? (activeBranches.length === 1 ? activeBranches[0] : null), open)
  const sellablePlans = usePlanOptions(open).filter((plan) => plan.sellable)
    .filter((plan) => !denied.has(plan.id))

  const currency = user?.tenant.currency ?? getCurrencyCode()

  const handleClose = () => {
    form.resetFields()
    setResult(null)
    setContactError(null)
    onClose()
  }

  /**
   * Kontaktni yaratadi. Qaytaradi: `null` — hammasi joyida yoki
   * yozadigan narsa yo'q; aks holda operatorga ko'rsatiladigan sabab.
   *
   * ⚠️ So'rov tanasi shu yerda QURILMAYDI — `buildContactRequest` da.
   * Backendda abonent+kontakt zanjiri hali yozilyapti va shakl o'zgarsa
   * o'sha bitta funksiya o'zgaradi.
   */
  const saveContact = async (
    ownerId: string,
    values: ContactFieldValues | undefined,
  ): Promise<string | null> => {
    if (!canWriteContact) return null
    const request = buildContactRequest({
      ownerType: 'SUBSCRIBER',
      ownerId,
      values: values ?? {},
      current: null,
    })
    if (request.kind !== 'create') return null
    try {
      await createContact(request.body)
      return null
    } catch (error) {
      return t('contact.createFailed', { reason: translateError(error, t) })
    }
  }

  const handleSubmit = () => {
    submitOnValid(form, async (values) => {
      setSubmitting(true)
      setResult(null)
      setContactError(null)
      try {
        const request = {
          subscriber: {
            login: values.login.trim(),
            password: poolVisp ? null : values.password,
            displayName: null,
            phone: normalizePhone(values.phone),
            kind: values.kind,
            // ⚠️ Bo'sh yuborilgani «egasiz» degani EMAS: chaqiruvchining
            // o'zi agent bo'lsa server maydonni o'z tugunidan to'ldiradi
            // (`SubscriberOwnership.resolveForCreate`). Tenant darajasidagi
            // operator uchun esa u ROSTDAN bo'sh qoladi — va aynan shu
            // holat 74 abonentdan 74 tasini «taqsimlanmagan» qilib
            // qo'ygan edi, chunki forma bu maydonni umuman so'ramasdi.
            agentId: values.agentId ?? null,
            simultaneousUse: values.simultaneousUse ?? null,
            staticIp: values.staticIp?.trim() || null,
            profile: profileDraft(values.profile ?? {}),
            vispId: canPickVisp ? (values.vispId ?? null) : null,
            branchNodeId: values.branchNodeId ?? null,
          },
          planId: values.planId ?? null,
          // Pul MINOR birlikda ketadi — `MoneyInput` allaqachon shunday beradi
          topUpMinor: values.topUp?.amount ?? 0,
          currency: values.topUp?.currency ?? currency,
          note: values.note?.trim() || null,
        }
        const outcome = poolVisp
          ? await registerPoolAccount(poolVisp.id, request)
          : await provisionSubscriber(request)

        setResult(outcome.result)

        // ⚠️ `OK` emas, `didChangeState`: `PARTIAL` da ham abonent YARATILGAN
        // (faqat RADIUS proyeksiyasi yoki sessiya qismi yetmagan). Ro'yxatni
        // yangilamaslik uni ekranda ko'rinmas qilardi.
        if (didChangeState(outcome.result)) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.subscribers.all })
          if (poolVisp) void queryClient.invalidateQueries({ queryKey: queryKeys.network.all })
        }

        // ⚠️ ABONENT `xsib-subscriber` DA, KONTAKT `xsib-platform` DA —
        // bitta tranzaksiya YO'Q (ADR-016), ya'ni yarim holat MUMKIN.
        //
        // ⚠️ Shart `OK` EMAS, `didChangeState`: `PARTIAL` da ham abonent
        // ROSTDAN yaratilgan. Faqat `OK` da yozish operator kiritgan
        // manzil va telefonni jimgina yo'qotardi — oyna esa sariq
        // banner bilan ochiq turaverardi va u yerda kontakt haqida bir
        // og'iz ham gap bo'lmasdi.
        const contactFailure =
          didChangeState(outcome.result) && outcome.subscriber
            ? await saveContact(outcome.subscriber.id, values.contact)
            : null
        setContactError(contactFailure)

        // Muvaffaqiyatda darhol kartochkaga o'tamiz — operator odatda
        // shu zahoti parol yoki paketni tekshiradi. `PARTIAL` da esa
        // MODAL OCHIQ QOLADI: sariq ogohlantirish o'qilmasdan yopilib
        // ketmasligi kerak. Kontakt yiqilgani ham xuddi shunday ushlab
        // qoladi — aks holda ogohlantirish ko'rinmasdan yo'qolardi.
        if (outcome.result.status === 'OK' && outcome.subscriber && contactFailure === null) {
          const id = outcome.subscriber.id
          handleClose()
          navigate(`/subscribers/${id}`)
        }
      } catch (error) {
        setResult({ status: 'FAILED', message: translateError(error, t) })
      } finally {
        setSubmitting(false)
      }
    })
  }

  const selectedPlanId = Form.useWatch('planId', form)
  const selectedPlan = sellablePlans.find((plan) => plan.id === selectedPlanId)

  /**
   * BUG-01 — "summa bo'sh" holati tushunarsiz xato berardi.
   *
   * Zanjir shunday edi: paket tanlanadi, summa bo'sh qoldiriladi,
   * `topUpMinor: 0` ketadi. Backend to'g'ri va batafsil rad javob
   * beradi ("Kiritilgan summa paket narxidan kam. Kerak: X,
   * kiritilgan: 0"), lekin u HTTP 422 bilan keladi va matn yo'lda
   * yo'qolardi (`subscriberApi.unprocessableBody` izohiga qarang) —
   * operator «So'rov to'g'ri, lekin bajarib bo'lmadi» degan matnni
   * ko'rardi.
   *
   * ⚠️ `unprocessableBody` tuzatildi, ya'ni server matni endi yetib
   * keladi. Lekin bu tekshiruv baribir kerak va u ustun turadi:
   *   · xabar OPERATOR TILIDA va formatlangan pul bilan bo'ladi
   *     (server minor birlikdagi xom son beradi: "350000");
   *   · xato maydonning YONIDA chiqadi, modal tepasida emas;
   *   · abonent umuman yaratilmaydi — server yo'lida esa u
   *     yaratilib, paketsiz `NEW` holatda qolardi.
   *
   * ⚠️ Paket TANLANMAGAN bo'lsa summa MAJBURIY EMAS: "faqat hisob
   * ochish" to'liq haqiqiy stsenariy (modal javadoc'i).
   *
   * ⚠️ Bu yerdagi narx — OPERATORNING narxi (`usePlanOptions`), server
   * esa abonent resellerining narxini oladi. Ular farq qilishi mumkin,
   * lekin faqat BIR TOMONGA: reseller narxi ota narxidan PAST bo'la
   * olmaydi (`PlanServiceImpl.setResellerPrice`). Ya'ni bu tekshiruv
   * har doim xavfsiz QUYI chegara — noto'g'ri rad etmaydi, oz-moz
   * yumshoq bo'lishi mumkin, qolganini server ushlaydi.
   */
  const validateTopUp = (_rule: unknown, value: Money | null | undefined): Promise<void> => {
    if (!selectedPlan) return Promise.resolve()
    const entered = value?.amount ?? 0
    if (entered >= selectedPlan.price.amount) return Promise.resolve()
    const price = formatMoneyValue(selectedPlan.price)
    return Promise.reject(
      new Error(
        entered === 0
          ? t('subscriber.sale.amountRequired', { price })
          : t('subscriber.sale.amountTooSmall', { price }),
      ),
    )
  }

  return (
    <Modal
      open={open}
      title={t('operation.createTitle')}
      onCancel={handleClose}
      onOk={handleSubmit}
      okText={t('operation.create')}
      cancelText={t('common.cancel')}
      confirmLoading={submitting}
      destroyOnHidden
      width={760}
    >
      <Typography.Paragraph type="secondary">{t('operation.createSubtitle')}</Typography.Paragraph>

      {result ? (
        <div style={{ marginBottom: 16 }}>
          <OperationResultAlert result={result} />
        </div>
      ) : null}

      {contactError ? (
        // ⚠️ `warning`, `error` EMAS: abonent ROSTDAN yaratilgan va uni
        // qayta yaratishga urinish «bunday login bor» xatosini berardi.
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('contact.saveFailedTitle')}
          description={contactError}
        />
      ) : null}

      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark
        initialValues={{
          kind: 'PPPOE' satisfies DeliveryMethod,
          ...(activeBranches.length === 1 ? { branchNodeId: activeBranches[0] } : {}),
        }}
        disabled={submitting}
      >
        {showBranch ? (
          <Form.Item
            name="branchNodeId"
            label={t('branch.label')}
            extra={tenantWide ? t('branch.createHint') : undefined}
            rules={branchRequired ? [{ required: true, message: t('branch.chooseRequired') }] : []}
          >
            <Select<string>
              allowClear={!branchRequired}
              showSearch
              optionFilterProp="label"
              placeholder={t('branch.choose')}
              options={branchChoices.map((b) => ({ value: b.id, label: branchLabel(b) }))}
            />
          </Form.Item>
        ) : null}
        {canReadVisps && (canPickVisp || vispOptions.some((v) => v.pooled)) ? (
          <Form.Item name="vispId" label={t('subscriber.visp.label')} extra={t('subscriber.visp.createHint')}>
            <VispSelect
              assignableOnly
              pooledOnly={!canPickVisp}
              allowClear
              onChange={() => form.setFieldValue('login', undefined)}
              placeholder={defaultVisp ? `${defaultVisp.centerName} / ${defaultVisp.name}` : undefined}
            />
          </Form.Item>
        ) : null}

        {poolVisp ? (
          <Alert type="info" showIcon style={{ marginBottom: 16 }} message={t('subscriber.pool.poolHint')} />
        ) : null}

        <SubscriberProfileFields mode="create" />

        <SubscriberFormFields
          mode="create"
          showDisplayName={false}
          accountPicker={poolVisp ? <PoolAccountSelect vispId={poolVisp.id} /> : undefined}
        >
          <Form.Item
            name="planId"
            label={t('operation.sale.packageLabel')}
            extra={t('operation.sale.noPackageHint')}
          >
            <Select
              allowClear
              placeholder={t('operation.sale.noPackage')}
              options={sellablePlans.map((plan) => ({
                value: plan.id,
                label: `${displayName(plan, i18n.language)} — ${formatMoneyValue(plan.price)}`,
              }))}
            />
          </Form.Item>

          {selectedPlan && selectedPlan.policyId === null ? (
            // "Faqat kvota beradigan paket" — tezlikka tegmaydi. Buni
            // aytmaslik operatorni "nega tarif o'zgarmadi?" degan savolga
            // olib kelardi.
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={t('catalog.plan.quotaOnlyTitle')}
              // ⚠️ Matn UZUN va u SABABNI aytadi — qisqartirish taqiqlangan
              // (QARORLAR.md §23.2, 6-qoida). Shuning uchun yashiriladi:
              // sarlavha va birinchi ikki qator ko'rinadi, qolgani
              // «Batafsil» ostida (XSB-004).
              description={<ExpandableHint text={t('catalog.plan.quotaOnlyHint')} fontSize={14} />}
            />
          ) : null}

          <Form.Item
            name="topUp"
            label={t('billing.topUp.field')}
            // ⚠️ Talab paket TANLANGANDA paydo bo'ladi — shuning uchun
            // `required` bayrog'i ham shartli: yulduzcha faqat u
            // haqiqatan majburiy bo'lganda ko'rinsin.
            required={selectedPlan !== undefined}
            // Paket almashtirilganda summa QAYTA tekshiriladi — aks holda
            // arzon paket uchun kiritilgan summa qimmatrog'iga o'tganda
            // ham "to'g'ri" bo'lib qolardi.
            dependencies={['planId']}
            extra={
              selectedPlan
                ? t('subscriber.sale.amountNeeded', {
                    price: formatMoneyValue(selectedPlan.price),
                  })
                : t('operation.sale.amountHint')
            }
            rules={[{ validator: validateTopUp }]}
          >
            <MoneyInput currency={currency} />
          </Form.Item>
        </SubscriberFormFields>

        <Form.Item name="note" label={t('billing.topUp.comment')}>
          <Input.TextArea rows={2} maxLength={200} />
        </Form.Item>

        {/*
          KONTAKT — viloyat, tuman, manzil, telefon, email (ADR-031).

          ⚠️ Huquqi yo'q operatorga KO'RSATILMAYDI: u maydonlarni
          to'ldirib, keyin «abonent yaratildi, kontakt saqlanmadi»
          ogohlantirishini olardi va sababini tushunmasdi.
        */}
        {canWriteContact ? <ContactFields group="contact" /> : null}
      </Form>
    </Modal>
  )
}
